import React from "react";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import StopIcon from "@mui/icons-material/Stop";
import VideocamIcon from "@mui/icons-material/Videocam";
import TuneIcon from "@mui/icons-material/Tune";
import ShowChartOutlinedIcon from "@mui/icons-material/ShowChartOutlined";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import FavoriteIcon from "@mui/icons-material/Favorite";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import TouchAppIcon from "@mui/icons-material/TouchApp";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import { useLongPress } from "../../hooks/useLongPress";
import {
  formatPlayerStats,
  normalizeExerciseRole,
} from "../../utils/liveTrainingPlayerContext";
import {
  latestBpmFromSamples,
  playerHasCollectingHr,
} from "../../utils/liveHrHistoryBuffer";
import { LiveHrTrendPanel } from "../../components/LiveTraining/LiveHrTrendPanel";
import "./LiveTrainingTableReplayReview.css";

function roleModifier(role) {
  const key = role ? String(role).toLowerCase() : "";
  if (key === "sparring" || key.includes("spar")) return "sparring";
  if (key === "both") return "both";
  if (
    key === "active" ||
    key === "performance" ||
    key.includes("actif") ||
    key.includes("perform")
  ) {
    return "active";
  }
  return null;
}

function roleBadgeClass(role, prefix = "lt-coach-table__role") {
  const mod = roleModifier(role);
  return mod ? `${prefix} ${prefix}--${mod}` : prefix;
}

function PlayerCell({
  player,
  side,
  align,
  t,
  selectable = false,
  allowEmptySelect = false,
  selected = false,
  swapTarget = false,
  dimmed = false,
  flash = false,
  hideSwapBadges = false,
  swapState = null,
  disabled = false,
  onClick,
  onSwapRetry,
  voiceHoldEnabled = false,
  onVoiceHold,
  globalRecordingMode = null,
  activeRecording = null,
  onStartVoiceRecording = null,
  onStartVideoRecording = null,
  onTriggerTableVideo = null,
  onStopCapture = null,
  tableNumber = null,
  hrTrendMode = false,
  hrSamples = [],
  liveBpm = null,
  mhr = 180,
  onEditPersonalized = null,
}) {
  const empty = !player?.name && !player?.playerId;
  const hasClassement = Boolean(player?.classement || player?.fltt_classement);
  const classementValue = player?.classement || player?.fltt_classement || null;
  const rankingPoints = player?.rankingPoints ?? player?.ranking_points;
  const stats = player
    ? formatPlayerStats(classementValue, rankingPoints, t)
    : null;
  const pointsLabel = player
    ? formatPlayerStats(hasClassement ? null : classementValue, rankingPoints, t)
    : null;
  const roleLabel = player
    ? player.roleLabel || normalizeExerciseRole(player.role, t)
    : null;
  const showHrChip =
    playerHasCollectingHr(hrSamples) ||
    (liveBpm != null && liveBpm > 0);
  const liveBpmDisplay =
    showHrChip ? liveBpm ?? latestBpmFromSamples(hrSamples) : null;
  const isPending = swapState?.status === "pending";
  const needsRetry =
    swapState?.status === "timeout" || swapState?.status === "error";
  const interactive =
    selectable && !disabled && !isPending && (!empty || allowEmptySelect);
  const isSwappablePick =
    selectable &&
    !hrTrendMode &&
    !selected &&
    !swapTarget &&
    !isPending &&
    !needsRetry &&
    Boolean(player?.playerId);
  const canRecord = voiceHoldEnabled && !empty && player?.userId;
  const canRecordVideo =
    globalRecordingMode === "video" &&
    !empty &&
    (typeof onTriggerTableVideo === "function" ||
      (canRecord && typeof onStartVideoRecording === "function"));
  // Video mode selects whole tables for autoeval capture; voice still taps players.
  const pickTarget = Boolean(
    ((globalRecordingMode === "voice" && canRecord) || canRecordVideo) &&
      !activeRecording
  );

  const handleRecordingTap = (e) => {
    if (globalRecordingMode === "video" && canRecordVideo) {
      e.stopPropagation();
      if (typeof onTriggerTableVideo === "function") {
        onTriggerTableVideo();
        return;
      }
      if (activeRecording && activeRecording.playerId === player.playerId) {
        onStopCapture?.();
        return;
      }
      if (activeRecording) return;
      onStartVideoRecording?.({
        playerId: player.playerId,
        userId: player.userId,
        tableNumber,
      });
      return;
    }
    if (globalRecordingMode !== "voice" || !canRecord) {
      onClick?.(e);
      return;
    }
    e.stopPropagation();
    if (activeRecording && activeRecording.playerId === player.playerId) {
      onStopCapture?.();
      return;
    }
    if (activeRecording) return;

    onStartVoiceRecording?.({
      playerId: player.playerId,
      userId: player.userId,
      tableNumber,
    });
  };

  const longPress = useLongPress({
    disabled: (!canRecord && !canRecordVideo) || Boolean(globalRecordingMode),
    onLongPress: (e) => {
      e.stopPropagation();
      onVoiceHold?.(player);
    },
    onClick: handleRecordingTap,
  });

  const isRecordingThisPlayer = activeRecording && activeRecording.playerId === player?.playerId;

  const swapFeedbackBadge =
    hideSwapBadges
      ? null
      : selectable && !hrTrendMode && isPending
        ? (
            <span
              className="lt-coach-table__swap-badge lt-coach-table__swap-badge--swapping"
              role="status"
            >
              <AutorenewIcon
                className="lt-coach-table__swap-spin"
                fontSize="inherit"
                aria-hidden
              />
              {t("liveTrainingTableSwapPending") || t("submitting")}
            </span>
          )
        : selectable && !hrTrendMode && selected
          ? (
              <span
                className="lt-coach-table__swap-badge lt-coach-table__swap-badge--selected"
                role="status"
              >
                <CheckCircleOutlineIcon fontSize="inherit" aria-hidden />
                {t("liveTrainingTableSwapSelected")}
              </span>
            )
          : selectable && !hrTrendMode && swapTarget && interactive
            ? (
                <span className="lt-coach-table__swap-badge lt-coach-table__swap-badge--target">
                  <TouchAppIcon fontSize="inherit" aria-hidden />
                  {t("liveTrainingTableSwapTapTarget")}
                </span>
              )
            : null;

  const nameNode = (
    <span
      className={`lt-coach-table__name${
        canRecord || canRecordVideo ? " lt-coach-table__name--voice-hold" : ""
      }`}
      {...(canRecord || canRecordVideo
        ? {
            onPointerDown: longPress.onPointerDown,
            onPointerUp: longPress.onPointerUp,
            onPointerLeave: longPress.onPointerLeave,
            onPointerCancel: longPress.onPointerCancel,
            onClick: longPress.onClick,
            title: globalRecordingMode
              ? globalRecordingMode === "voice"
                ? t("liveTrainingCoachRecordingPickVoice") ||
                  "Tap to start voice recording"
                : t("liveTrainingCoachRecordingPickVideo") ||
                  "Tap to start video recording"
              : t("liveTrainingFeedbackVoiceHoldHint"),
          }
        : {})}
    >
      {isRecordingThisPlayer ? (
        <button
          type="button"
          className="lt-coach-table__stop-capture-btn"
          onClick={(e) => {
            e.stopPropagation();
            onStopCapture?.();
          }}
        >
          <FiberManualRecordIcon className="lt-recording-indicator blinking" color="error" fontSize="small" />
          <StopIcon fontSize="small" /> Stop Capture
        </button>
      ) : empty ? (
        t("empty") || "Empty"
      ) : (
        player.name || "—"
      )}
    </span>
  );

  const content = hrTrendMode ? (
    <>
      <div className="lt-coach-table__player-meta">
        <span className="lt-coach-table__side">{side}</span>
        {roleLabel ? (
          <span className={roleBadgeClass(player?.role)}>{roleLabel}</span>
        ) : null}
      </div>
      {!empty && showHrChip ? (
        <LiveHrTrendPanel
          player={player}
          role={roleLabel}
          samples={hrSamples}
          mhr={mhr}
          layout="compact"
          liveBpm={liveBpmDisplay}
        />
      ) : empty ? (
        <span className="lt-coach-table__name">{t("empty") || "Empty"}</span>
      ) : (
        <>
          {nameNode}
          {stats ? (
            <span className="lt-coach-table__stats">{stats}</span>
          ) : null}
        </>
      )}
    </>
  ) : (
    <>
      {swapFeedbackBadge}
      {!empty ? (
        <div className="lt-coach-table__player-meta">
          <span className="lt-coach-table__side">{side}</span>
          {roleLabel ? (
            <span className={roleBadgeClass(player?.role)}>{roleLabel}</span>
          ) : null}
        </div>
      ) : null}
      {nameNode}
      {!empty && (hasClassement || pointsLabel) ? (
        <span className="lt-coach-table__rating">
          {hasClassement ? (
            <span className="lt-coach-table__classement">
              {String(classementValue)}
            </span>
          ) : null}
          {pointsLabel ? (
            <span className="lt-coach-table__stats">{pointsLabel}</span>
          ) : null}
        </span>
      ) : null}
      {showHrChip ? (
        <span className="lt-coach-table__hr-chip" aria-label={`${liveBpmDisplay ?? "—"} BPM`}>
          <FavoriteIcon className="lt-coach-table__hr-icon" fontSize="inherit" />
          <span>{liveBpmDisplay ?? "—"}</span>
        </span>
      ) : null}
      {!empty && (player?.personalizedExerciseName || player?.personalizedMissing) ? (
        <span className="lt-coach-table__personalized">
          <span className="lt-coach-table__personalized-name">
            {player.personalizedExerciseName ||
              t("personalizedSlotMissingShort", "No assigned exercise")}
          </span>
          {onEditPersonalized ? (
            <button
              type="button"
              className="lt-coach-table__personalized-edit"
              onClick={(e) => {
                e.stopPropagation();
                onEditPersonalized(player);
              }}
              title={t("personalizedSlotUpdate", "Change this player's exercise")}
              aria-label={t("personalizedSlotUpdate", "Change this player's exercise")}
            >
              <EditOutlinedIcon fontSize="inherit" />
            </button>
          ) : null}
        </span>
      ) : null}
      {needsRetry ? (
        <button
          type="button"
          className="lt-coach-table__swap-retry"
          onClick={(e) => {
            e.stopPropagation();
            onSwapRetry?.(swapState.opId);
          }}
        >
          {swapState?.error && String(swapState.error).startsWith("liveTraining")
            ? t(swapState.error) || t("liveTrainingTableSwapRetry")
            : t("liveTrainingTableSwapRetry") || t("kotCoachSubmitRetry") || "Retry"}
        </button>
      ) : null}
    </>
  );

  const playerClass = [
    `lt-coach-table__player lt-coach-table__player--${align}`,
    interactive ? "lt-coach-table__player--btn" : "",
    isSwappablePick ? "lt-coach-table__player--swappable" : "",
    selected ? "lt-coach-table__player--selected" : "",
    swapTarget && interactive && !selected
      ? "lt-coach-table__player--target"
      : "",
    dimmed ? "lt-coach-table__player--dimmed" : "",
    flash ? "lt-coach-table__player--flash" : "",
    isPending ? " lt-coach-table__player--swapping" : "",
    needsRetry ? " lt-coach-table__player--swap-warn" : "",
    empty && !interactive ? " lt-coach-table__player--empty" : "",
    pickTarget ? " lt-coach-table__player--pick-target" : "",
    isRecordingThisPlayer ? " lt-coach-table__player--recording" : "",
    hrTrendMode ? " lt-coach-table__player--hr-trend" : "",
    hasClassement ? " lt-coach-table__player--has-classement" : "",
  ]
    .filter(Boolean)
    .join("");

  if (interactive) {
    return (
      <div
        role="button"
        tabIndex={disabled ? -1 : 0}
        aria-disabled={disabled || undefined}
        aria-pressed={selected || undefined}
        className={playerClass}
        onClick={disabled ? undefined : onClick}
        onKeyDown={
          disabled
            ? undefined
            : (e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  onClick?.(e);
                }
              }
        }
      >
        {content}
      </div>
    );
  }

  return <div className={playerClass}>{content}</div>;
}

function UnassignedSeatList({
  players,
  t,
  align = "left",
  layout = "stack",
  disabled = false,
  onAssign = null,
}) {
  if (!players?.length) return null;
  const canAssign = typeof onAssign === "function" && !disabled;
  return (
    <div
      className={`lt-coach-table__open-seat lt-coach-table__open-seat--${layout} lt-coach-table__open-seat--${align}`}
      aria-label={t("liveTrainingTableWizardUnassigned") || "Unassigned players"}
    >
      <span className="lt-coach-table__open-seat-label">
        {t("liveTrainingTableWizardUnassigned") || "Unassigned players"}
      </span>
      <ul className="lt-coach-table__open-seat-list">
        {players.map((p) => {
          const id = p.playerId ?? p.player_id ?? p.id;
          const name = p.name || "—";
          const stats = p.stats || "";
          const content = (
            <>
              <span className="lt-coach-table__open-seat-name">{name}</span>
              {stats ? (
                <span className="lt-coach-table__open-seat-stats">{stats}</span>
              ) : null}
            </>
          );
          return (
            <li key={id ?? name}>
              {canAssign ? (
                <button
                  type="button"
                  className="lt-coach-table__open-seat-btn"
                  title={t("liveTrainingTableSeatUnassigned", {
                    name,
                    defaultValue: `Seat ${name} on this table`,
                  })}
                  onClick={(e) => {
                    e.stopPropagation();
                    onAssign(p);
                  }}
                >
                  {content}
                </button>
              ) : (
                <span className="lt-coach-table__open-seat-btn">{content}</span>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function shortPlayerLabel(player) {
  if (!player) return "";
  const first = String(player.firstName || player.user_first_name || "").trim();
  const last = String(player.lastName || player.user_last_name || "").trim();
  const alias = String(player.alias || player.user_alias || "").trim();
  const full = String(player.name || "").trim();
  if (alias) return alias;
  if (first) return first;
  if (full) return full.split(/\s+/)[0];
  return last || "—";
}

export function CoachTablePreviewCard({
  tableNumber,
  player1,
  player2,
  player3 = null,
  extras = null,
  t,
  headExtra = null,
  className = "",
  /** Subgroup / station accent (hex or css color) for quick visual grouping. */
  accentColor = null,
  subgroupLabel = null,
  swapMode = false,
  allowEmptySelect = false,
  selectedSlot = null,
  /** Slots eligible as swap destinations (multi-list + dual). */
  targetSlots = null,
  swapTarget = false,
  /** Per-slot target override; falls back to card-level swapTarget. */
  player1SwapTarget = null,
  player2SwapTarget = null,
  player3SwapTarget = null,
  player1Dimmed = false,
  player2Dimmed = false,
  player3Dimmed = false,
  player1Flash = false,
  player2Flash = false,
  /** Avoid badge height shifts (e.g. dedicated swap dialog). */
  hideSwapBadges = false,
  disabled = false,
  onSelectPlayer1,
  onSelectPlayer2,
  onSelectPlayer3 = null,
  onSelectExtra = null,
  onTableClick = null,
  selected = false,
  scoreA = null,
  scoreB = null,
  winnerSide = null,
  done = false,
  voiceHoldEnabled = false,
  onPlayerVoiceHold = null,
  player1SwapState = null,
  player2SwapState = null,
  onSwapRetry = null,
  globalRecordingMode = null,
  activeRecording = null,
  onStartVoiceRecording = null,
  onStartVideoRecording = null,
  onStopCapture = null,
  tableVideoSelected = false,
  tableVideoRecording = false,
  tableVideoStarting = false,
  tableCameraConnected = false,
  tableCameraOnline = false,
  onToggleTableVideo = null,
  onStartTableVideo = null,
  onStopTableVideo = null,
  onCalibrateCamera = null,
  replayEnabled = false,
  replayArmed = false,
  /** A phone at this table is recording the exercise's evaluation video. */
  evalRecording = false,
  replayStatus = null,
  replayNotice = null,
  onReplayTable = null,
  hrTrendMode = false,
  onToggleHrTrend = null,
  player1Samples = [],
  player2Samples = [],
  player1LiveBpm = null,
  player2LiveBpm = null,
  mhr = 180,
  onEditPersonalizedPlayer = null,
  identityUnconfirmed = false,
  /** Recorded evaluation clips on this table (finalized exercise review). */
  evaluationVideoCount = 0,
  onPlayEvaluationVideo = null,
  /** Present players with no table. Shown in an empty seat. */
  unassignedPlayers = null,
  /** Seat one of those players on this table. */
  onAssignUnassigned = null,
}) {
  const hasScore =
    scoreA != null ||
    scoreB != null ||
    (done && (scoreA === 0 || scoreB === 0));
  const scoreRow =
    hasScore && (scoreA != null || scoreB != null) ? (
      <div className="lt-coach-table__score-row" aria-label={t("score") || "Score"}>
        <span
          className={
            winnerSide === "A"
              ? "lt-coach-table__score-win"
              : "lt-coach-table__score-lose"
          }
        >
          {scoreA ?? "—"}
        </span>
        <span className="lt-coach-table__score-sep">–</span>
        <span
          className={
            winnerSide === "B"
              ? "lt-coach-table__score-win"
              : "lt-coach-table__score-lose"
          }
        >
          {scoreB ?? "—"}
        </span>
      </div>
    ) : null;

  const tableHasHr =
    playerHasCollectingHr(player1Samples) ||
    playerHasCollectingHr(player2Samples) ||
    (player1LiveBpm != null && player1LiveBpm > 0) ||
    (player2LiveBpm != null && player2LiveBpm > 0);
  const showHrToggle =
    tableHasHr && typeof onToggleHrTrend === "function";

  const hrTrendToggle = showHrToggle ? (
    <button
      type="button"
      className="lt-coach-table__hr-toggle"
      onClick={(e) => {
        e.stopPropagation();
        onToggleHrTrend();
      }}
      aria-pressed={hrTrendMode}
      aria-label={
        hrTrendMode
          ? t("liveTrainingPanelModeDefault") || "Show player info"
          : t("liveTrainingPanelModeHrTrend") || "Show heart rate trend"
      }
      title={
        hrTrendMode
          ? t("liveTrainingPanelModeDefault") || "Show player info"
          : t("liveTrainingPanelModeHrTrend") || "Show heart rate trend"
      }
    >
      {hrTrendMode ? (
        <PersonOutlineIcon fontSize="small" />
      ) : (
        <ShowChartOutlinedIcon fontSize="small" />
      )}
    </button>
  ) : null;

  const canTriggerTableVideo =
    typeof onStartTableVideo === "function" ||
    typeof onToggleTableVideo === "function";

  const triggerTableVideoAction = () => {
    if (tableVideoRecording || tableVideoStarting) {
      if (typeof onStopTableVideo === "function") {
        onStopTableVideo({ tableNumber });
      } else {
        onToggleTableVideo?.({ tableNumber });
      }
      return;
    }
    if (typeof onStartTableVideo === "function") {
      onStartTableVideo({ tableNumber });
    } else {
      onToggleTableVideo?.({ tableNumber });
    }
  };

  const showTableVideoSlot =
    tableVideoRecording ||
    tableVideoStarting ||
    (globalRecordingMode === "video" &&
      tableCameraConnected &&
      canTriggerTableVideo &&
      !activeRecording);

  // Hide Replay whenever the coach is in a recording mode (video/voice) or
  // when table video recording is active on this table.
  const showReplaySlot =
    replayEnabled &&
    !evalRecording &&
    !showTableVideoSlot &&
    !globalRecordingMode &&
    !tableVideoRecording &&
    !tableVideoStarting &&
    (replayArmed || replayStatus === "pending" || replayNotice);

  const showEvalVideoBtn =
    evaluationVideoCount > 0 && typeof onPlayEvaluationVideo === "function";
  const evalVideoLabel = t(
    "liveTrainingPlayEvaluationVideo",
    "Play evaluation video"
  );
  const evalVideoBtn = showEvalVideoBtn ? (
    <button
      type="button"
      className="lt-coach-table__eval-video"
      onPointerDown={(e) => e.stopPropagation()}
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        onPlayEvaluationVideo({ tableNumber });
      }}
      aria-label={evalVideoLabel}
      title={evalVideoLabel}
    >
      <PlayCircleOutlineIcon fontSize="small" />
      {evaluationVideoCount > 1 ? (
        <span className="lt-coach-table__eval-video-count">
          {evaluationVideoCount}
        </span>
      ) : null}
    </button>
  ) : null;

  const seatOccupied = (p) =>
    Boolean(p?.playerId || p?.player_id || p?.name);
  const listedPlayers = [
    seatOccupied(player1)
      ? { player: player1, side: "A", slot: "player1", onClick: onSelectPlayer1 }
      : null,
    seatOccupied(player2)
      ? { player: player2, side: "B", slot: "player2", onClick: onSelectPlayer2 }
      : null,
    seatOccupied(player3)
      ? {
          player: player3,
          side: "C",
          slot: "player3",
          onClick: onSelectPlayer3,
        }
      : null,
    ...(Array.isArray(extras)
      ? extras.map((p, i) =>
          seatOccupied(p)
            ? {
                player: p,
                side: String.fromCharCode(68 + Math.min(i, 22)), // D…
                slot: `extra:${i}`,
                onClick: () => onSelectExtra?.(p, i),
              }
            : null
        )
      : []),
  ].filter(Boolean);

  const openSeatPlayers =
    !hrTrendMode &&
    Array.isArray(unassignedPlayers) &&
    unassignedPlayers.length > 0
      ? unassignedPlayers
      : null;
  const seat1Open = !seatOccupied(player1);
  const seat2Open = !seatOccupied(player2);
  const showOpenSeatList = Boolean(openSeatPlayers) && (seat1Open || seat2Open);
  const assignUnassigned =
    showOpenSeatList &&
    swapMode &&
    !disabled &&
    typeof onAssignUnassigned === "function"
      ? onAssignUnassigned
      : null;

  // Use compact A/B/C/D list whenever a third+ seat is occupied; dual A-vs-B only for ≤2.
  const multiMode = listedPlayers.length > 2;
  const playerClassement = (p) => p?.classement || p?.fltt_classement || null;
  const hasClassementOnTable = listedPlayers.some((row) =>
    Boolean(playerClassement(row.player))
  );
  // 3+: always two columns — first half left, second half right
  const playerRows = Math.max(1, Math.ceil(listedPlayers.length / 2));
  const leftPlayers = listedPlayers.slice(0, playerRows);
  const rightPlayers = listedPlayers.slice(playerRows);
  const slotSelected = (slot) =>
    Array.isArray(selectedSlot)
      ? selectedSlot.includes(slot)
      : selectedSlot === slot;
  const slotIsTarget = (slot) => {
    if (Array.isArray(targetSlots) && targetSlots.includes(slot)) return true;
    if (slot === "player1" && player1SwapTarget != null) {
      return Boolean(player1SwapTarget);
    }
    if (slot === "player2" && player2SwapTarget != null) {
      return Boolean(player2SwapTarget);
    }
    if (slot === "player3" && player3SwapTarget != null) {
      return Boolean(player3SwapTarget);
    }
    return Boolean(swapTarget && !slotSelected(slot));
  };
  const slotDimmed = (slot) => {
    if (slot === "player1") return Boolean(player1Dimmed);
    if (slot === "player2") return Boolean(player2Dimmed);
    if (slot === "player3") return Boolean(player3Dimmed);
    return false;
  };

  const body = (
    <>
      <div className="lt-coach-table__head">
        <span className="lt-coach-table__number-cluster">
          <span className="lt-coach-table__number">
            {t("table_with_number", { number: tableNumber ?? "—" })}
          </span>
          {identityUnconfirmed ? (
            <span className="lt-coach-table__identity">
              {t("playerIdentityUnconfirmed", "Identity unconfirmed")}
            </span>
          ) : null}
          {multiMode ? (
            <span className="lt-coach-table__seat-count">
              {listedPlayers.length}
            </span>
          ) : null}
        </span>
        {subgroupLabel ? (
          <span className="lt-coach-table__subgroup" title={subgroupLabel}>
            {subgroupLabel}
          </span>
        ) : null}
        {tableCameraConnected && typeof onCalibrateCamera === "function" ? (
          <span className="lt-coach-table__replay-wrap">
            <button
              type="button"
              className="lt-coach-table__replay"
              onPointerDown={(e) => e.stopPropagation()}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onCalibrateCamera({ tableNumber });
              }}
              title={t(
                "liveTrainingCoachCameraCalibration",
                "Player camera"
              )}
              aria-label={t("liveTrainingCalibrateCamera", "Calibrate")}
            >
              <TuneIcon fontSize="inherit" />
              {t("liveTrainingCalibrateCamera", "Calibrate")}
            </button>
          </span>
        ) : null}
        {showTableVideoSlot ? (
          <span className="lt-coach-table__replay-wrap">
            {tableVideoRecording ? (
              <button
                type="button"
                className="lt-coach-table__replay lt-coach-table__replay--stop"
                onPointerDown={(e) => e.stopPropagation()}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  triggerTableVideoAction();
                }}
                title={t("liveTrainingTableVideoStop", "Stop table camera recording")}
              >
                <FiberManualRecordIcon
                  className="lt-recording-indicator blinking"
                  fontSize="inherit"
                />
                <StopIcon fontSize="inherit" />
                <span>{t("stop", "Stop")}</span>
              </button>
            ) : tableVideoStarting ? (
              <button
                type="button"
                className="lt-coach-table__replay lt-coach-table__replay--starting"
                onPointerDown={(e) => e.stopPropagation()}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  triggerTableVideoAction();
                }}
                title={t("liveTrainingTableVideoStarting", "Starting camera recording…")}
              >
                <FiberManualRecordIcon
                  className="lt-recording-indicator blinking"
                  fontSize="inherit"
                />
                <span>{t("starting", "Starting…")}</span>
              </button>
            ) : (
              <button
                type="button"
                className="lt-coach-table__replay lt-coach-table__replay--record"
                onPointerDown={(e) => e.stopPropagation()}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  triggerTableVideoAction();
                }}
                title={t(
                  "liveTrainingTableVideoStartThis",
                  "Start recording on this table camera"
                )}
              >
                {tableCameraOnline ? (
                  <span
                    className="lt-coach-table__camera-online-dot"
                    aria-hidden="true"
                  />
                ) : null}
                <VideocamIcon fontSize="inherit" />
                <span>{t("record", "Record")}</span>
              </button>
            )}
          </span>
        ) : showReplaySlot ? (
          <span className="lt-coach-table__replay-wrap">
            {replayArmed ? (
              <span className="lt-coach-table__replay-dot" title={t("liveTrainingReplayReady", "Replay ready")} />
            ) : null}
            {replayStatus === "pending" ? (
              <span className="lt-coach-table__replay-status">
                {t("liveTrainingReplayPreparing", "Preparing last 10s…")}
              </span>
            ) : (
              <button
                type="button"
                className="lt-coach-table__replay"
                onPointerDown={(e) => e.stopPropagation()}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onReplayTable?.({ tableNumber });
                }}
              >
                {t("liveTrainingReplayTable", "Replay")}
              </button>
            )}
            {replayNotice && replayStatus !== "pending" ? (
              <span className="lt-coach-table__replay-error" role="status">
                {replayNotice}
              </span>
            ) : null}
          </span>
        ) : null}
        {evalVideoBtn}
        {hrTrendToggle}
        {headExtra}
      </div>
      {multiMode ? (
        <div
          className={[
            "lt-coach-table__list",
            "lt-coach-table__list--halves",
            listedPlayers.length > 8 ? "lt-coach-table__list--scroll" : "",
          ]
            .filter(Boolean)
            .join(" ")}
          aria-label={t("players") || "Players"}
        >
          {[leftPlayers, rightPlayers].map((col, colIdx) => (
            <ul key={colIdx === 0 ? "left" : "right"} className="lt-coach-table__list-col">
              {col.map((row) => {
                const p = row.player;
                const niveau = playerClassement(p);
                const pointsOnly = formatPlayerStats(
                  null,
                  p.rankingPoints ?? p.ranking_points,
                  t
                );
                const roleLabel = p.roleLabel || normalizeExerciseRole(p.role, t);
                const ratingParts = [
                  niveau ? String(niveau) : null,
                  pointsOnly,
                  roleLabel,
                ].filter(Boolean);
                const selected = slotSelected(row.slot);
                const isTarget = slotIsTarget(row.slot) && !selected;
                const dimmed = slotDimmed(row.slot);
                const canRecordVoiceRow =
                  globalRecordingMode === "voice" &&
                  voiceHoldEnabled &&
                  Boolean(p?.userId);
                const canRecordVideoRow =
                  globalRecordingMode === "video" && showTableVideoSlot;
                const interactive =
                  (swapMode || canRecordVoiceRow || canRecordVideoRow) &&
                  !hrTrendMode &&
                  !disabled &&
                  !dimmed;
                return (
                  <li key={row.slot}>
                    <button
                      type="button"
                      className={[
                        "lt-coach-table__list-row",
                        niveau || pointsOnly
                          ? "lt-coach-table__list-row--classement"
                          : "",
                        selected ? "is-selected" : "",
                        isTarget ? "is-target" : "",
                        dimmed ? "is-dimmed" : "",
                        interactive ? "is-interactive" : "",
                      ]
                        .filter(Boolean)
                        .join(" ")}
                      disabled={!interactive}
                      aria-pressed={selected || undefined}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (canRecordVideoRow) {
                          triggerTableVideoAction();
                          return;
                        }
                        if (canRecordVoiceRow) {
                          if (
                            activeRecording &&
                            activeRecording.playerId === p.playerId
                          ) {
                            onStopCapture?.();
                            return;
                          }
                          if (!activeRecording) {
                            onStartVoiceRecording?.({
                              playerId: p.playerId,
                              userId: p.userId,
                              tableNumber,
                            });
                          }
                          return;
                        }
                        row.onClick?.(e);
                      }}
                    >
                      <span className="lt-coach-table__list-side">{row.side}</span>
                      <span className="lt-coach-table__list-main">
                        <span className="lt-coach-table__list-name">
                          {p.name || shortPlayerLabel(p) || "—"}
                        </span>
                        {ratingParts.length ? (
                          <span className="lt-coach-table__list-meta lt-coach-table__rating">
                            {niveau ? (
                              <span className="lt-coach-table__classement">
                                {String(niveau)}
                              </span>
                            ) : null}
                            {pointsOnly ? (
                              <span className="lt-coach-table__stats">
                                {pointsOnly}
                              </span>
                            ) : null}
                            {roleLabel ? (
                              <span
                                className={roleBadgeClass(
                                  p.role,
                                  "lt-coach-table__list-role"
                                )}
                              >
                                {roleLabel}
                              </span>
                            ) : null}
                          </span>
                        ) : null}
                        {p.personalizedExerciseName || p.personalizedMissing ? (
                          <span className="lt-coach-table__personalized">
                            <span className="lt-coach-table__personalized-name">
                              {p.personalizedExerciseName ||
                                t("personalizedSlotMissingShort", "No assigned exercise")}
                            </span>
                            {onEditPersonalizedPlayer ? (
                              <button
                                type="button"
                                className="lt-coach-table__personalized-edit"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onEditPersonalizedPlayer(p);
                                }}
                                title={t("personalizedSlotUpdate", "Change this player's exercise")}
                              >
                                <EditOutlinedIcon fontSize="inherit" />
                              </button>
                            ) : null}
                          </span>
                        ) : null}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          ))}
        </div>
      ) : null}
      {multiMode && showOpenSeatList ? (
        <UnassignedSeatList
          players={openSeatPlayers}
          t={t}
          layout="span"
          disabled={disabled}
          onAssign={assignUnassigned}
        />
      ) : null}
      {!multiMode ? (
        showOpenSeatList && seat1Open && seat2Open ? (
          <UnassignedSeatList
            players={openSeatPlayers}
            t={t}
            layout="span"
            disabled={disabled}
            onAssign={assignUnassigned}
          />
        ) : (
          <div
            className={`lt-coach-table__row${
              showOpenSeatList ? " lt-coach-table__row--open-seat" : ""
            }`}
          >
            {showOpenSeatList && seat1Open ? (
              <UnassignedSeatList
                players={openSeatPlayers}
                t={t}
                align="left"
                disabled={disabled}
                onAssign={assignUnassigned}
              />
            ) : (
              <PlayerCell
                player={player1}
                side="A"
                align="left"
                t={t}
                selectable={swapMode && !hrTrendMode}
                allowEmptySelect={allowEmptySelect && swapMode}
                selected={slotSelected("player1")}
                swapTarget={slotIsTarget("player1")}
                dimmed={player1Dimmed}
                flash={player1Flash}
                hideSwapBadges={hideSwapBadges}
                swapState={player1SwapState}
                disabled={disabled}
                onClick={onSelectPlayer1}
                onSwapRetry={onSwapRetry}
                voiceHoldEnabled={voiceHoldEnabled && !hrTrendMode}
                onVoiceHold={onPlayerVoiceHold}
                globalRecordingMode={globalRecordingMode}
                activeRecording={activeRecording}
                onStartVoiceRecording={onStartVoiceRecording}
                onStartVideoRecording={onStartVideoRecording}
                onTriggerTableVideo={
                  showTableVideoSlot ? triggerTableVideoAction : null
                }
                onStopCapture={onStopCapture}
                tableNumber={tableNumber}
                hrTrendMode={hrTrendMode}
                hrSamples={player1Samples}
                liveBpm={player1LiveBpm}
                mhr={mhr}
                onEditPersonalized={onEditPersonalizedPlayer}
              />
            )}
            <span className="lt-coach-table__vs">{t("vs")}</span>
            {showOpenSeatList && seat2Open ? (
              <UnassignedSeatList
                players={openSeatPlayers}
                t={t}
                align="right"
                disabled={disabled}
                onAssign={assignUnassigned}
              />
            ) : (
              <PlayerCell
                player={player2}
                side="B"
                align="right"
                t={t}
                selectable={swapMode && !hrTrendMode}
                allowEmptySelect={allowEmptySelect && swapMode}
                selected={slotSelected("player2")}
                swapTarget={slotIsTarget("player2")}
                dimmed={player2Dimmed}
                flash={player2Flash}
                hideSwapBadges={hideSwapBadges}
                swapState={player2SwapState}
                disabled={disabled}
                onClick={onSelectPlayer2}
                onSwapRetry={onSwapRetry}
                voiceHoldEnabled={voiceHoldEnabled && !hrTrendMode}
                onVoiceHold={onPlayerVoiceHold}
                globalRecordingMode={globalRecordingMode}
                activeRecording={activeRecording}
                onStartVoiceRecording={onStartVoiceRecording}
                onStartVideoRecording={onStartVideoRecording}
                onTriggerTableVideo={
                  showTableVideoSlot ? triggerTableVideoAction : null
                }
                onStopCapture={onStopCapture}
                tableNumber={tableNumber}
                hrTrendMode={hrTrendMode}
                hrSamples={player2Samples}
                liveBpm={player2LiveBpm}
                mhr={mhr}
                onEditPersonalized={onEditPersonalizedPlayer}
              />
            )}
          </div>
        )
      ) : null}
      {scoreRow}
    </>
  );

  const pickTableForVideo =
    globalRecordingMode === "video" &&
    tableCameraConnected &&
    canTriggerTableVideo &&
    !activeRecording &&
    !tableVideoRecording &&
    !tableVideoStarting;

  const tableClickHandler =
    onTableClick ||
    (pickTableForVideo || tableVideoRecording
      ? () => triggerTableVideoAction()
      : null);

  const rootClass = [
    "lt-coach-table",
    accentColor ? "lt-coach-table--accent" : "",
    tableClickHandler ? "lt-coach-table--interactive" : "",
    selected || tableVideoSelected ? "lt-coach-table--selected" : "",
    done ? "lt-coach-table--done" : "",
    hrTrendMode ? "lt-coach-table--hr-trend" : "",
    tableVideoRecording || tableVideoStarting ? "lt-coach-table--recording" : "",
    tableVideoSelected && !tableVideoRecording
      ? "lt-coach-table--video-selected"
      : "",
    pickTableForVideo ? "lt-coach-table--pick-video" : "",
    showTableVideoSlot || showReplaySlot
      ? "lt-coach-table--replay"
      : "",
    swapMode ? "lt-coach-table--swap-mode" : "",
    showOpenSeatList ? "lt-coach-table--open-seat" : "",
    multiMode ? "lt-coach-table--multi" : "",
    hasClassementOnTable ? "lt-coach-table--has-classement" : "",
    className,
  ]
    .filter(Boolean)
    .join(" ");

  const rootStyle = accentColor
    ? { "--lt-table-accent": accentColor }
    : undefined;

  // Seat taps and a table tap can both be active (move onto the table, swap with a seat).
  // Keep a <div> when inline Record/Stop buttons are present (no nested buttons).
  if (
    tableClickHandler &&
    !swapMode &&
    !replayEnabled &&
    !showTableVideoSlot &&
    !showEvalVideoBtn
  ) {
    return (
      <button
        type="button"
        className={rootClass}
        style={rootStyle}
        disabled={disabled || done}
        onClick={tableClickHandler}
        title={
          pickTableForVideo
            ? t(
                "liveTrainingCoachRecordingPickTable",
                "Tap to start table camera recording"
              )
            : undefined
        }
      >
        {body}
      </button>
    );
  }

  const hasNestedActionButtons =
    showTableVideoSlot || showReplaySlot || showEvalVideoBtn || showHrToggle;

  return (
    <div
      className={rootClass}
      style={rootStyle}
      onClick={
        tableClickHandler && !disabled && !done
          ? (e) => {
              const hit = e.target?.closest?.("button, [role='button']");
              if (hit && hit !== e.currentTarget) return;
              tableClickHandler(e);
            }
          : undefined
      }
      role={
        tableClickHandler && !hasNestedActionButtons ? "button" : undefined
      }
      tabIndex={
        tableClickHandler && !hasNestedActionButtons && !disabled && !done
          ? 0
          : undefined
      }
      title={
        pickTableForVideo
          ? t(
              "liveTrainingCoachRecordingPickTable",
              "Tap to start table camera recording"
            )
          : undefined
      }
    >
      {body}
    </div>
  );
}

export default CoachTablePreviewCard;
